import { useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useRestClient } from "../core/api/RestClientContext";
import { RefreshBtn } from "../core/components/Button";
import { Card } from "../core/components/Card";
import Dropdown, { DropdownOption } from "../core/components/DropDown";
import {
	ServerStatsPoint,
	ServerStatsResponse,
} from "../core/models/stats";
import { XuiServerConfig } from "../core/models/xui";
import {
	TimeSeriesChart,
	TimeSeriesPoint,
} from "../domains/stats/components/charts/TimeSeriesChart";
import { HostMultiSelect } from "../domains/xui/components/HostMultiSelect";
import "./StatsPage.css";

const PERIOD_OPTIONS: DropdownOption[] = [
	{ value: "1d", label: "1d" },
	{ value: "7d", label: "7d" },
	{ value: "30d", label: "30d" },
];

function getFromParam(period: string): string {
	const d = new Date();
	const days = period === "1d" ? 1 : period === "7d" ? 7 : 30;
	d.setDate(d.getDate() - days);
	return d.toISOString();
}

interface StatsSectionProps {
	title: string;
	subtitle: string;
	headerActions: React.ReactNode;
	error: string | null;
	loading: boolean;
	hasData: boolean;
	emptyMessage: string;
	children: React.ReactNode;
}

const StatsSection: React.FC<StatsSectionProps> = ({
	title,
	subtitle,
	headerActions,
	error,
	loading,
	hasData,
	emptyMessage,
	children,
}) => (
	<section className="stats-section">
		<div className="stats-header">
			<div className="stats-header-text">
				<h2>{title}</h2>
				<p className="stats-subtitle">{subtitle}</p>
			</div>
			<div className="stats-header-actions">{headerActions}</div>
		</div>
		{error && <div className="stats-error">{error}</div>}
		{!loading && !error && !hasData && (
			<div className="stats-empty">{emptyMessage}</div>
		)}
		{hasData && children}
	</section>
);

const STEP_OPTIONS: DropdownOption[] = [
	{ value: "PT1M", label: "1m" },
	{ value: "PT5M", label: "5m" },
	{ value: "PT30M", label: "30m" },
	{ value: "PT1H", label: "1h" },
	{ value: "P1D", label: "1d" },
	{ value: "P7D", label: "7d" },
	{ value: "P30D", label: "30d" },
];

const AGG_OPTIONS: DropdownOption[] = [
	{ value: "MAX", label: "MAX" },
	{ value: "AVG", label: "AVG" },
];

function formatTimestamp(date: Date, period: string): string {
	if (period === "1d") {
		return date.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
	}
	if (period === "7d") {
		return date.toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
	}
	// 30d
	return date.toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit" });
}

function buildNetIOChartData(points: ServerStatsPoint[], period: string): TimeSeriesPoint[] {
	return [...points]
		.sort(
			(a, b) =>
				new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
		)
		.map((p) => ({
			timestamp: formatTimestamp(new Date(p.createdAt), period),
			up: p.systemInfo?.netIO?.up ?? 0,
			down: p.systemInfo?.netIO?.down ?? 0,
		}));
}

export const useHostsCache = () => {
	const queryClient = useQueryClient();
	const invalidate = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: ["xui-hosts"] });
	}, [queryClient]);
	return { invalidateHostsCache: invalidate };
};

export const StatsPage: React.FC = () => {
	const client = useRestClient();
	const [stats, setStats] = useState<ServerStatsResponse | null>(null);
	const [netioLoading, setNetioLoading] = useState(false);
	const [netioError, setNetioError] = useState<string | null>(null);
	const [step, setStep] = useState<string>("PT1H");
	const [period, setPeriod] = useState<string>("7d");
	const [agg, setAgg] = useState<"MAX" | "AVG">("MAX");
	const [selectedHostIds, setSelectedHostIds] = useState<string[]>([]);

	const { data: hosts = [], isLoading: hostsLoading } = useQuery<XuiServerConfig[]>(
		{
			queryKey: ["xui-hosts"],
			queryFn: () => client.getXuiHosts(),
			staleTime: 1000 * 60 * 5,
		},
	);

	useEffect(() => {
		if (!hostsLoading && hosts.length > 0 && selectedHostIds.length === 0) {
			setSelectedHostIds(hosts.map((h) => h.identity));
		}
	}, [hosts, hostsLoading, selectedHostIds]);

	const loadNetIO = useCallback(async () => {
		try {
			setNetioLoading(true);
			setNetioError(null);
			const from = getFromParam(period);
			const data = await client.getServerStats({
				step,
				from,
				agg,
				hosts: selectedHostIds,
			});
			setStats(data);
		} catch (e: any) {
			setNetioError(e?.message ?? "Не удалось загрузить статистику NetIO");
		} finally {
			setNetioLoading(false);
		}
	}, [client, step, period, agg, selectedHostIds]);

	useEffect(() => {
		if (selectedHostIds.length === 0) return;
		void loadNetIO();
	}, [loadNetIO, selectedHostIds]);

	const netioHasData = useMemo(
		() => !!stats && Object.keys(stats).length > 0,
		[stats],
	);
	const netioEntries = useMemo(
		() => (stats ? Object.entries(stats) : []),
		[stats],
	);

	const periodLabel =
		PERIOD_OPTIONS.find((o) => o.value === period)?.label ?? period;
	const stepLabel = STEP_OPTIONS.find((o) => o.value === step)?.label ?? step;

	return (
		<div className="stats-screen">
			<StatsSection
				title="Статистика NetIO"
				subtitle={`Трафик up/down по серверам (шаг: ${stepLabel}, период: ${periodLabel})`}
				headerActions={
					<>
						<HostMultiSelect
							hosts={hosts}
							selectedIds={selectedHostIds}
							onChange={setSelectedHostIds}
							disabled={netioLoading || hostsLoading}
						/>
						<Dropdown
							options={PERIOD_OPTIONS}
							selectedValue={period}
							onSelect={(opt) => opt && setPeriod(opt.value)}
							placeholder="Период"
							disabled={netioLoading}
							className="stats-period-dropdown"
						/>
						<Dropdown
							options={STEP_OPTIONS}
							selectedValue={step}
							onSelect={(opt) => opt && setStep(opt.value)}
							placeholder="Шаг"
							disabled={netioLoading}
							className="stats-step-dropdown"
						/>
						<Dropdown
							options={AGG_OPTIONS}
							selectedValue={agg}
							onSelect={(opt) => opt && setAgg(opt.value as "MAX" | "AVG")}
							placeholder="Агрегация"
							disabled={netioLoading}
							className="stats-agg-dropdown"
						/>
						<RefreshBtn
							onClick={() => void loadNetIO()}
							loading={netioLoading}
							showLabel
						/>
					</>
				}
				error={netioError}
				loading={netioLoading}
				hasData={netioHasData}
				emptyMessage="Нет данных статистики NetIO за выбранный период"
			>
				<div className="stats-grid">
					{netioEntries.map(([hostId, points]) => {
						if (!points || points.length === 0) return null;
						const chartData = buildNetIOChartData(points, period);
						const identity = points[0]?.systemInfo?.identity;
						return (
							<Card key={hostId} className="stats-card">
								<div className="stats-card-header">
									<div>
										<div className="stats-host-id">{hostId}</div>
										{identity && (
											<div className="stats-host-identity">{identity}</div>
										)}
									</div>
								</div>
								<div className="stats-card-body">
									<TimeSeriesChart
										data={chartData}
										series={[
											{ key: "up", name: "Up", color: "#869bdf" },
											{ key: "down", name: "Down", color: "#ecc689" },
										]}
										yAxisLabel="NetIO"
										height={220}
										formatAsBytesPerSec
									/>
								</div>
							</Card>
						);
					})}
				</div>
			</StatsSection>
		</div>
	);
};
